"use strict";
import { jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
export const PAGE_SNAPSHOT_OPT_IN_KEY = "__isPageSnapshotEnabled";
export const PAGE_AGENT_CHAT_OPT_IN_KEY = "__isAgentChatPageEnabled";
const EmptyContext = {};
const AgentChatContext = createContext({
  pageContext: EmptyContext,
  publish: () => void 0,
  unpublish: () => void 0
});
export const AgentChatContextProvider = ({ children }) => {
  const [contributions, setContributions] = useState({});
  const pageContext = useMemo(() => {
    const merged = {};
    for (const contribution of Object.values(contributions)) {
      Object.assign(merged, contribution);
    }
    return merged;
  }, [contributions]);
  const publish = useCallback((contributorId, context) => {
    setContributions((previous) => ({ ...previous, [contributorId]: context }));
  }, []);
  const unpublish = useCallback((contributorId) => {
    setContributions((previous) => {
      if (!(contributorId in previous)) return previous;
      const next = { ...previous };
      delete next[contributorId];
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({ pageContext, publish, unpublish }),
    [pageContext, publish, unpublish]
  );
  return /* @__PURE__ */ jsx(AgentChatContext.Provider, { value, children });
};
export const useAgentChatContext = (context) => {
  var _a;
  const contributorId = useId();
  const { publish, unpublish } = useContext(AgentChatContext);
  let serializedContext;
  try {
    serializedContext = (_a = JSON.stringify(context != null ? context : {})) != null ? _a : "";
  } catch {
    serializedContext = Object.keys(context != null ? context : {}).join(",");
  }
  const contextRef = useRef(context);
  contextRef.current = context;
  useEffect(() => {
    var _a2;
    publish(contributorId, (_a2 = contextRef.current) != null ? _a2 : {});
  }, [contributorId, publish, serializedContext]);
  useEffect(() => {
    return () => unpublish(contributorId);
  }, [contributorId, unpublish]);
};
export const useAgentChatPageContext = () => {
  return useContext(AgentChatContext).pageContext;
};
